import { randomBytes } from 'node:crypto';
import express, { type ErrorRequestHandler, type NextFunction, type Request, type Response } from 'express';
import rateLimit from 'express-rate-limit';
import helmet from 'helmet';
import { existsSync, readFileSync } from 'node:fs';
import path from 'node:path';
import { renderRobotsTxt, renderSitemapXml } from '../../shared/src/seo';
import {
  SAFE_MESSAGES,
  scanRequestSchema,
  scanSuccessSchema,
} from '../../shared/src/index';
import { type AppConfig, defaultConfig, normalizeConfig } from './config/env';
import { logError, logInfo } from './logging/logger';
import { contentSecurityPolicy } from './security/csp';
import { ConcurrencyLimiter } from './security/concurrency';
import { BusyError, HttpError } from './security/errors';
import { ADS_TXT } from './services/adsense';
import { createDnsResolver } from './services/dns-resolver';
import { performPinnedRequest } from './services/http-fetcher';
import { applyPageSeo, publicOrigin, stampScriptNonces } from './services/page-seo';
import { runScan } from './services/scanner';
import { createSearchLog, type SearchRecorder } from './services/search-log';
import type { PerformRequest, ResolveHostname } from './types';

export interface AppDependencies {
  config?: Partial<AppConfig>;
  resolveHostname?: ResolveHostname;
  performRequest?: PerformRequest;
  now?: () => Date;
  indexHtml?: string;
  searchLog?: SearchRecorder;
}

function errorBody(code: string, message: string) {
  return { success: false as const, error: { code, message } };
}

function clientDistPath(): string {
  return path.resolve(process.cwd(), 'client', 'dist');
}

export function createApp(dependencies: AppDependencies = {}) {
  const config = normalizeConfig({ ...defaultConfig, ...dependencies.config });
  const resolveHostname = dependencies.resolveHostname ?? createDnsResolver(config.timeoutMs);
  const performRequest = dependencies.performRequest ?? performPinnedRequest;
  const now = dependencies.now ?? (() => new Date());
  const concurrency = new ConcurrencyLimiter(config.maxConcurrency);
  const searchLog = dependencies.searchLog ?? createSearchLog(config.searchLogPath);

  const app = express();
  app.disable('x-powered-by');
  app.set('trust proxy', config.trustProxy ? 1 : false);

  app.use(
    helmet({
      contentSecurityPolicy: false,
      strictTransportSecurity: config.enableHsts ? undefined : false,
      crossOriginEmbedderPolicy: false,
      crossOriginOpenerPolicy: { policy: 'same-origin-allow-popups' },
    }),
  );

  app.use((_req, res, next) => {
    res.setHeader('Content-Security-Policy', contentSecurityPolicy({ enableHsts: config.enableHsts }));
    next();
  });

  app.use((req, res, next) => {
    const origin = req.header('origin');
    if (origin && origin === config.clientOrigin) {
      res.setHeader('Access-Control-Allow-Origin', config.clientOrigin);
      res.setHeader('Vary', 'Origin');
      res.setHeader('Access-Control-Allow-Methods', 'GET, POST, OPTIONS');
      res.setHeader('Access-Control-Allow-Headers', 'Content-Type, Accept');
      res.setHeader('Access-Control-Max-Age', '600');
    }
    if (req.method === 'OPTIONS') {
      res.status(204).end();
      return;
    }
    next();
  });

  app.use(express.json({ limit: '16kb', strict: true }));

  app.use('/api', (_req, res, next) => {
    res.setHeader('X-Robots-Tag', 'noindex');
    next();
  });

  app.get('/ads.txt', (_req, res) => {
    res.type('text/plain').send(ADS_TXT);
  });

  app.get('/robots.txt', (req, res) => {
    const origin = publicOrigin(config.siteUrl, req.get('host'), req.protocol);
    res.type('text/plain').send(renderRobotsTxt(origin));
  });

  app.get('/sitemap.xml', (req, res) => {
    const origin = publicOrigin(config.siteUrl, req.get('host'), req.protocol);
    res.type('application/xml').send(renderSitemapXml(origin));
  });

  app.use((req, res, next) => {
    if (req.method !== 'GET' && req.method !== 'HEAD') {
      next();
      return;
    }
    if (req.path.length > 1 && req.path.endsWith('/')) {
      const query = req.originalUrl.includes('?') ? req.originalUrl.slice(req.originalUrl.indexOf('?')) : '';
      res.redirect(301, `${req.path.replace(/\/+$/, '')}${query}`);
      return;
    }
    if (req.path === '/index.html') {
      res.redirect(301, '/');
      return;
    }
    next();
  });

  app.get('/api/health', (_req, res) => {
    res.json({ success: true, status: 'ok', service: 'php2shell-scanner' });
  });

  app.get('/api/stats', (_req, res, next) => {
    void searchLog
      .total()
      .then((total) => {
        res.json({ success: true, total });
      })
      .catch((error: unknown) => {
        logError('search_log_unreadable', { name: error instanceof Error ? error.name : 'Error' });
        next(error);
      });
  });

  const scanLimiter = rateLimit({
    windowMs: config.rateLimitWindowMs,
    limit: config.rateLimitMax,
    standardHeaders: true,
    legacyHeaders: false,
    statusCode: 429,
    handler: (_req, res) => {
      res.status(429).json(errorBody('rate_limited', SAFE_MESSAGES.rateLimited));
    },
  });

  app.post('/api/scan', scanLimiter, (req, res, next) => {
    void handleScan(req, res).catch(next);
  });

  async function handleScan(req: Request, res: Response) {
    const parsed = scanRequestSchema.safeParse(req.body);
    if (!parsed.success) {
      const field = parsed.error.issues[0]?.path[0];
      const message = field === 'authorized' ? SAFE_MESSAGES.unauthorized : SAFE_MESSAGES.invalidRequest;
      res.status(400).json(errorBody('validation_error', message));
      return;
    }

    try {
      const scan = await concurrency.run(() =>
        runScan(parsed.data.url, {
          config,
          resolveHostname,
          performRequest,
          now,
        }),
      );
      const body = { success: true as const, scan };
      const checked = scanSuccessSchema.safeParse(body);
      if (!checked.success) {
        logError('invalid_report_shape', {
          count: checked.error.issues.length,
          fields: checked.error.issues
            .slice(0, 6)
            .map((issue) => issue.path.join('.'))
            .join(','),
        });
        res.status(500).json(errorBody('internal_error', SAFE_MESSAGES.internal));
        return;
      }
      logInfo('scan_completed', {
        classification: scan.classification,
        httpStatus: scan.httpStatus,
        durationMs: scan.durationMs,
        truncated: scan.bodyTruncated,
      });
      try {
        await searchLog.record({
          checkedAt: scan.scannedAt,
          url: scan.submittedUrl,
          classification: scan.classification,
        });
      } catch (error) {
        logError('search_log_failed', { name: error instanceof Error ? error.name : 'Error' });
      }
      res.status(200).json(checked.data);
    } catch (error) {
      if (error instanceof BusyError) {
        res.status(503).json(errorBody('busy', error.message));
        return;
      }
      if (error instanceof HttpError) {
        res.status(error.status).json(errorBody(error.code, error.message));
        return;
      }
      logError('scan_failed', { name: error instanceof Error ? error.name : 'Error' });
      res.status(500).json(errorBody('internal_error', SAFE_MESSAGES.internal));
    }
  }

  app.use('/api', (_req, res) => {
    res.status(404).json(errorBody('not_found', SAFE_MESSAGES.notFound));
  });

  const indexTemplate = loadIndexTemplate(config.serveStatic, dependencies.indexHtml);
  if (config.serveStatic) {
    const dist = clientDistPath();
    if (existsSync(dist)) {
      app.use(express.static(dist, { index: false, maxAge: '1h', fallthrough: true }));
    } else {
      logError('static_missing', { missing: true });
    }
  }
  if (indexTemplate) {
    app.use((req, res, next) => {
      if (req.method !== 'GET' && req.method !== 'HEAD') {
        next();
        return;
      }
      if (req.path.startsWith('/api') || path.extname(req.path)) {
        next();
        return;
      }
      const origin = publicOrigin(config.siteUrl, req.get('host'), req.protocol);
      const page = applyPageSeo(indexTemplate, req.path, origin);
      const scriptNonce = randomBytes(16).toString('base64');
      res
        .status(page.status)
        .setHeader('Content-Security-Policy', contentSecurityPolicy({ enableHsts: config.enableHsts, scriptNonce }))
        .setHeader('Cache-Control', 'no-cache')
        .type('html')
        .send(stampScriptNonces(page.html, scriptNonce));
    });
  }

  const onError: ErrorRequestHandler = (error, _req, res, next: NextFunction) => {
    if (res.headersSent) {
      next(error);
      return;
    }
    const record = error as { type?: string; status?: number };
    if (record.type === 'entity.too.large' || record.status === 413) {
      res.status(413).json(errorBody('payload_too_large', SAFE_MESSAGES.payloadTooLarge));
      return;
    }
    if (error instanceof SyntaxError) {
      res.status(400).json(errorBody('validation_error', SAFE_MESSAGES.invalidJson));
      return;
    }
    logError('express_error', { name: error instanceof Error ? error.name : 'Error' });
    res.status(500).json(errorBody('internal_error', SAFE_MESSAGES.internal));
  };
  app.use(onError);

  return app;
}

function loadIndexTemplate(serveStatic: boolean, override?: string): string | null {
  if (override) return override;
  if (!serveStatic) return null;
  const indexHtml = path.join(clientDistPath(), 'index.html');
  if (!existsSync(indexHtml)) return null;
  return readFileSync(indexHtml, 'utf8');
}
